import { toast } from 'react-hot-toast';
import { ChangeEvent, useEffect, useState } from 'react';
import CheckBox from '../../../../components/common/CheckBox/CheckBox';
import { Button } from '../../../../components/ui/Button/Button';
import S from './AudioSchemaSettingsItemStyles';
import Modal from '../../../../components/common/Modal/Modal';
import InfoModal from '../../../../components/common/InfoModal/InfoModal';
import useGetAudioSchemas from '../../../../API/hooks/devices/useGetAudioSchemas';
import useGetFleetId from '../../../../API/hooks/useGetFleetId';
import useGetAudioSchemaFleet from '../../../../API/hooks/devices/useGetAudioSchemaFleet';
import Select from '../../../../components/common/Select/Select';
import usePostApplyAudioSchemaToFleet from '../../../../API/hooks/devices/usePostApplyAudioSchemaToFleet';
import usePatchActivateFleetAudioSchema from '../../../../API/hooks/devices/usePatchActivateFleetAudioSchema';
import useGetRole from '../../../../API/hooks/useGetRole';

type Props = {
  toggleModal: (closed: boolean) => void;
};

function AudioSchemaSettingsItem({
  toggleModal,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const {
    isFleetViewer,
    isFleetManager,
  } = useGetRole();
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  const { data: availableSchemas, isLoading: availableSchemasLoading } = useGetAudioSchemas(fleetId, '?limit=1000&skip=0');
  const { data: audioSchemaFleet, isLoading: audioSchemaFleetLoading } = useGetAudioSchemaFleet(fleetId);
  const { mutate: applySchema } = usePostApplyAudioSchemaToFleet(
    () => toast.success('Schema was applied successfully'),
    (e) => toast.error(`Schema applying error: ${e}`),
  );
  const { mutate: activateFleetSetting } = usePatchActivateFleetAudioSchema(
    () => toast.success('Fleet setting was updated successfully'),
    (e) => toast.error(`Setting update error: ${e}`),
  );
  const fleetSchemaIsActive = audioSchemaFleet?.isActive as boolean;
  const [isActive, setIsActive] = useState(fleetSchemaIsActive);
  const [selectedSchema, setSelectedSchema] = useState(audioSchemaFleet?.audioBundleId || '');

  const schemaIsChanged = audioSchemaFleet?.audioBundleId !== selectedSchema;
  const activeIsChanged = audioSchemaFleet?.isActive !== isActive;

  useEffect(() => setSelectedSchema(audioSchemaFleet?.audioBundleId || ''), [audioSchemaFleet]);

  const handleCheckboxChange = (e: ChangeEvent<any>, state: boolean): void => {
    setIsActive(state);
  };

  const defaultListItem = [{
    label: `${availableSchemasLoading || audioSchemaFleetLoading ? 'Loading...' : 'Select schema'}`,
    value: '',
  }];

  const schemasList = availableSchemas?.results.map((schema) => ({
    label: schema.name,
    value: schema._id,
  })) || [];

  const schemaOnChange = (e: any): void => {
    setSelectedSchema(e.target.value);
  };

  const saveAudioSettings = (): void => {
    if (schemaIsChanged && selectedSchema !== '') {
      applySchema({
        fleetId,
        audioBundleId: selectedSchema,
      });
    }
    if (activeIsChanged) {
      activateFleetSetting({
        fleetId,
        isActive,
      });
    }
  };

  return (
    <S.Wrapper>
      <p>Audio Schema Configuration:</p>
      <div className="change-schema">
        <p>Current schema:</p>
        <Select
          value={selectedSchema as string}
          options={[...defaultListItem, ...schemasList]}
          onChange={schemaOnChange}
          disabled={isFleetManager || isFleetViewer}
          simple
        />
      </div>
      <CheckBox
        name="isActive"
        state={isActive}
        label="Schema is active for fleet"
        onChange={handleCheckboxChange}
      />
      <div className="actions-container">
        <Button
          height="50px"
          width="135px"
          onClick={() => toggleModal(false)}
          type="button"
          disabled={isFleetManager || isFleetViewer}
        >
          Close
        </Button>
        <Button
          className="save-button"
          height="50px"
          width="135px"
          type="button"
          onClick={saveAudioSettings}
          disabled={availableSchemasLoading || audioSchemaFleetLoading || selectedSchema === '' || isFleetManager || isFleetViewer || !(schemaIsChanged || activeIsChanged)}
        >
          Save
        </Button>
      </div>
      {confirmSaveModalOpen && (
        <Modal
          title="Audio schema settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New audio schema was submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Audio schema settings"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default AudioSchemaSettingsItem;
